<template>
  <div class="box-border w-full h-full flex flex-col bg-[#eef1f0ff] rounded-[20px] overflow-hidden relative p-3 sm:p-5 lg:px-8 lg:py-5 gap-3 lg:gap-4">
    <div class="box-border w-full min-h-0 flex-1 flex flex-col gap-3 lg:gap-4 justify-start items-start">
      <!-- Mobile & Tablet: Status Filter Pills (Scrollable, all pills) -->
      <div
        class="flex lg:hidden box-border w-full overflow-x-auto hide-scrollbar flex-row gap-2 items-center py-1 shrink-0 -mx-1 px-1"
        :class="{'opacity-50 pointer-events-none': localEnrollments.length === 0}"
      >
        <div
          v-for="tab in allTabs"
          :key="tab.key"
          class="box-border shrink-0 h-fit flex flex-row gap-[6px] px-3 py-1.5 justify-center items-center rounded-full cursor-pointer transition-colors whitespace-nowrap"
          :class="activeTab === tab.key ? 'bg-[#9b9b9bff] border border-transparent' : 'bg-transparent border border-black/40 hover:bg-black/5'"
          @click="setTab(tab.key)"
        >
          <span class="text-[12px] sm:text-[13px] text-black font-[Raleway,sans-serif] font-medium leading-none whitespace-nowrap">
            {{ tab.label }}
          </span>
        </div>
      </div>

      <!-- Mobile & Tablet: Action Controls Row (Search, Filter, 3 circular kebab icons) -->
      <div class="flex lg:hidden box-border w-full flex-row gap-2 sm:gap-3 items-center justify-between shrink-0 py-0.5">
        <!-- Search Bar -->
        <div class="box-border flex-1 min-w-[120px] max-w-[260px] h-[38px] sm:h-[42px] bg-white rounded-full px-3 flex items-center gap-2 border border-black/10 shadow-none">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#5b5c60"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="shrink-0"
          >
            <circle
              cx="11"
              cy="11"
              r="8"
            />
            <line
              x1="21"
              y1="21"
              x2="16.65"
              y2="16.65"
            />
          </svg>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search submissions"
            class="text-[12px] sm:text-[13px] bg-transparent outline-none w-full text-[#5b5c60] font-[Raleway,sans-serif] font-normal"
          >
        </div>

        <!-- Right: Sort/Filter + 3 Action Icons -->
        <div class="flex items-center gap-2 sm:gap-2.5 shrink-0">
          <!-- Filter/Sort Button -->
          <div class="relative">
            <button
              type="button"
              class="w-[38px] h-[38px] sm:w-[42px] sm:h-[42px] rounded-full bg-white flex items-center justify-center border border-black/10 cursor-pointer hover:bg-gray-50 focus:outline-none"
              title="Sort Options"
              @click.stop="toggleSort"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#043f42"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line
                  x1="4"
                  y1="21"
                  x2="4"
                  y2="14"
                />
                <line
                  x1="4"
                  y1="10"
                  x2="4"
                  y2="3"
                />
                <line
                  x1="12"
                  y1="21"
                  x2="12"
                  y2="12"
                />
                <line
                  x1="12"
                  y1="8"
                  x2="12"
                  y2="3"
                />
                <line
                  x1="20"
                  y1="21"
                  x2="20"
                  y2="16"
                />
                <line
                  x1="20"
                  y1="12"
                  x2="20"
                  y2="3"
                />
                <line
                  x1="1"
                  y1="14"
                  x2="7"
                  y2="14"
                />
                <line
                  x1="9"
                  y1="8"
                  x2="15"
                  y2="8"
                />
                <line
                  x1="17"
                  y1="16"
                  x2="23"
                  y2="16"
                />
              </svg>
            </button>
            <!-- Sort Dropdown on mobile -->
            <div
              v-if="isSortOpen"
              class="absolute top-full right-0 mt-2 w-52 bg-white shadow-2xl rounded-[14px] z-50 flex flex-col py-2 border border-black/10 font-['Raleway',sans-serif]"
              @click.stop
            >
              <div class="px-3.5 py-1 text-[11px] text-gray-400 uppercase tracking-wider font-bold">
                Sort by Name
              </div>
              <button
                type="button"
                class="w-full text-left px-3.5 py-2 text-[13px] hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                :class="currentSort === 'name-asc' ? 'text-[#01d680] font-bold' : 'text-[#043f42]'"
                @click="setSort('name-asc')"
              >
                <span>Name (A - Z)</span>
                <span v-if="currentSort === 'name-asc'">✓</span>
              </button>
              <button
                type="button"
                class="w-full text-left px-3.5 py-2 text-[13px] hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                :class="currentSort === 'name-desc' ? 'text-[#01d680] font-bold' : 'text-[#043f42]'"
                @click="setSort('name-desc')"
              >
                <span>Name (Z - A)</span>
                <span v-if="currentSort === 'name-desc'">✓</span>
              </button>
              <div class="my-1 border-t border-gray-100" />
              <div class="px-3.5 py-1 text-[11px] text-gray-400 uppercase tracking-wider font-bold">
                Sort by Date
              </div>
              <button
                type="button"
                class="w-full text-left px-3.5 py-2 text-[13px] hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                :class="currentSort === 'date-desc' ? 'text-[#01d680] font-bold' : 'text-[#043f42]'"
                @click="setSort('date-desc')"
              >
                <span>Newest First</span>
                <span v-if="currentSort === 'date-desc'">✓</span>
              </button>
              <button
                type="button"
                class="w-full text-left px-3.5 py-2 text-[13px] hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                :class="currentSort === 'date-asc' ? 'text-[#01d680] font-bold' : 'text-[#043f42]'"
                @click="setSort('date-asc')"
              >
                <span>Oldest First</span>
                <span v-if="currentSort === 'date-asc'">✓</span>
              </button>
            </div>
          </div>

          <!-- Mobile Kebab Actions Menu (Export CSV, Download All, Email Template, Edit Mapping) -->
          <div
            class="relative shrink-0"
            @click.stop
          >
            <button
              id="mobile-programme-actions-kebab-btn"
              type="button"
              aria-haspopup="true"
              :aria-expanded="isMobileActionsKebabOpen"
              class="w-[38px] h-[38px] sm:w-[42px] sm:h-[42px] rounded-full bg-white flex items-center justify-center border border-black/10 text-[#043f42ff] hover:bg-gray-50 transition-colors focus:outline-none cursor-pointer"
              title="More actions"
              @click="isMobileActionsKebabOpen = !isMobileActionsKebabOpen"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <circle
                  cx="12"
                  cy="5"
                  r="2"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="2"
                />
                <circle
                  cx="12"
                  cy="19"
                  r="2"
                />
              </svg>
            </button>

            <!-- Kebab Context Menu Dropdown -->
            <div
              v-if="isMobileActionsKebabOpen"
              class="absolute top-[48px] right-0 w-[220px] bg-white shadow-2xl rounded-[16px] z-50 flex flex-col p-1.5 border border-black/10 font-['Raleway',sans-serif]"
              @click.stop
            >
              <!-- 1. Export CSV -->
              <button
                type="button"
                class="flex items-center gap-2.5 px-3 py-2 text-[13px] rounded-[10px] font-semibold transition-colors text-left border-none bg-transparent w-full"
                :class="(!programme.cohort?.sponsor_completed_at || localEnrollments.length === 0) ? 'text-gray-400 cursor-not-allowed opacity-50' : 'text-[#043f42ff] hover:bg-[#edf7f2] cursor-pointer'"
                @click="if (programme.cohort?.sponsor_completed_at && localEnrollments.length > 0) { emitExport(); isMobileActionsKebabOpen = false; }"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="shrink-0 text-[#043f42ff]"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line
                    x1="8"
                    y1="13"
                    x2="16"
                    y2="13"
                  />
                  <line
                    x1="8"
                    y1="17"
                    x2="16"
                    y2="17"
                  />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
                <span>Export CSV</span>
              </button>

              <!-- 2. Download All / ZIP -->
              <template v-if="freeTier">
                <subscription-gate class="w-full">
                  <button
                    type="button"
                    class="flex items-center gap-2.5 px-3 py-2 text-[13px] rounded-[10px] font-semibold transition-colors text-left border-none bg-transparent w-full"
                    :class="(!programme.cohort?.sponsor_completed_at || localEnrollments.length === 0) ? 'text-gray-400 cursor-not-allowed opacity-50' : 'text-[#043f42ff] hover:bg-[#edf7f2] cursor-pointer'"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="shrink-0 text-[#043f42ff]"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line
                        x1="12"
                        y1="15"
                        x2="12"
                        y2="3"
                      />
                    </svg>
                    <span>Download All</span>
                  </button>
                </subscription-gate>
              </template>
              <template v-else>
                <a
                  v-if="zipReadyUrl"
                  :href="zipReadyUrl"
                  download
                  class="flex items-center gap-2.5 px-3 py-2 text-[13px] text-[#01d680ff] hover:bg-[#edf7f2] rounded-[10px] font-semibold transition-colors no-underline cursor-pointer"
                  @click="resetZipState(); isMobileActionsKebabOpen = false;"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="shrink-0"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line
                      x1="12"
                      y1="15"
                      x2="12"
                      y2="3"
                    />
                  </svg>
                  <span>Download Ready ZIP</span>
                </a>
                <button
                  v-else
                  type="button"
                  class="flex items-center gap-2.5 px-3 py-2 text-[13px] rounded-[10px] font-semibold transition-colors text-left border-none bg-transparent w-full"
                  :class="(!programme.cohort?.sponsor_completed_at || localEnrollments.length === 0) ? 'text-gray-400 cursor-not-allowed opacity-50' : (isCompilingZip ? 'text-[#01d680ff] cursor-wait bg-[#01d680ff]/10' : 'text-[#043f42ff] hover:bg-[#edf7f2] cursor-pointer')"
                  @click="if (programme.cohort?.sponsor_completed_at) { emitDownloadAll(); isMobileActionsKebabOpen = false; }"
                >
                  <svg
                    v-if="isCompilingZip"
                    class="animate-spin h-4 w-4 text-[#043f42ff] shrink-0"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <svg
                    v-else
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="shrink-0 text-[#043f42ff]"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line
                      x1="12"
                      y1="15"
                      x2="12"
                      y2="3"
                    />
                  </svg>
                  <span>{{ isCompilingZip ? 'Compiling ZIP...' : 'Download All' }}</span>
                </button>
              </template>

              <!-- 3. Email Template -->
              <button
                type="button"
                class="flex items-center gap-2.5 px-3 py-2 text-[13px] text-[#043f42ff] hover:bg-[#edf7f2] rounded-[10px] font-semibold transition-colors text-left border-none bg-transparent w-full cursor-pointer"
                @click="openEmailModal(); isMobileActionsKebabOpen = false;"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="shrink-0 text-[#043f42ff]"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                <span>Email Template</span>
              </button>

              <!-- 4. Edit Programme Mapping -->
              <button
                type="button"
                class="flex items-center gap-2.5 px-3 py-2 text-[13px] rounded-[10px] font-semibold transition-colors text-left border-none bg-transparent w-full"
                :class="isMappingDisabled ? 'text-gray-400 cursor-not-allowed opacity-50' : 'text-[#043f42ff] hover:bg-[#edf7f2] cursor-pointer'"
                :title="isMappingDisabled ? 'Mapping cannot be edited once signing has begun.' : 'Edit field mapping'"
                @click="if (!isMappingDisabled) { goToMapping(); isMobileActionsKebabOpen = false; }"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="shrink-0 text-[#043f42ff]"
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                <span>Edit Programme Mapping</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Desktop Top Row: Pills on Left, Actions + Search/Filter on Right -->
      <div class="hidden lg:flex box-border w-full h-fit shrink-0 flex-row gap-[46px] justify-between items-center">
        <div class="box-border w-fit shrink-0 h-fit flex flex-row gap-[30px] justify-start items-center">
          <div
            class="box-border flex flex-row gap-[10px] items-center py-[2px] pr-[10px]"
            :class="{'opacity-50 pointer-events-none': localEnrollments.length === 0}"
          >
            <div
              v-for="tab in primaryTabs"
              :key="tab.key"
              class="box-border shrink-0 h-fit flex flex-row gap-[10px] p-[10px_16px] justify-center items-center rounded-full cursor-pointer transition-colors"
              :class="activeTab === tab.key ? 'bg-[#9b9b9bff] border border-transparent' : 'bg-transparent [border:1px_solid_#000000ff] hover:bg-black/5'"
              @click="setTab(tab.key)"
            >
              <div class="text-[14px]/[20px] box-border text-[#000000ff] font-[Raleway,system-ui,sans-serif] font-normal text-left whitespace-nowrap">
                {{ tab.label }}
              </div>
            </div>

            <!-- Dropdown / Remaining Statuses Pill -->
            <div
              class="relative shrink-0"
              @click.stop
            >
              <div
                class="box-border shrink-0 h-fit flex flex-row gap-[8px] p-[10px_16px] justify-center items-center rounded-full cursor-pointer transition-colors"
                :class="isDropdownTabActive ? 'bg-[#9b9b9bff] border border-transparent' : 'bg-transparent [border:1px_solid_#000000ff] hover:bg-black/5'"
                @click="toggleDropdown"
              >
                <div class="text-[14px]/[20px] box-border text-[#000000ff] font-[Raleway,system-ui,sans-serif] font-normal text-left whitespace-nowrap">
                  {{ currentDropdownLabel }}
                </div>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="text-[#000000ff] transition-transform"
                  :class="isDropdownOpen ? 'rotate-180' : ''"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </div>

              <!-- Dropdown Menu -->
              <div
                v-if="isDropdownOpen"
                class="absolute top-[48px] left-0 min-w-[170px] bg-white shadow-2xl rounded-[14px] z-50 flex flex-col py-2 border border-black/10 overflow-hidden"
              >
                <div
                  v-for="item in dropdownTabs"
                  :key="item.key"
                  class="px-[16px] py-[10px] text-[13px] font-[Raleway,system-ui,sans-serif] hover:bg-[#f5f5f5ff] cursor-pointer flex justify-between items-center transition-colors"
                  :class="activeTab === item.key ? 'text-[#01d680ff] font-bold bg-[#01d680ff]/5' : 'text-[#000000ff]'"
                  @click="selectDropdownTab(item.key)"
                >
                  <span>{{ item.label }}</span>
                  <span
                    v-if="activeTab === item.key"
                    class="text-[#01d680ff] font-bold"
                  >✓</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="box-border w-fit shrink-0 h-fit flex flex-row gap-[10px] sm:gap-[12px] justify-end items-center flex-wrap">
          <div
            v-if="isProgrammeFullyCompleted"
            class="box-border w-fit shrink-0 h-fit flex flex-row gap-[10px] p-[10px_16px] justify-center items-center rounded-full transition-colors bg-[#01d680ff] opacity-60 cursor-not-allowed"
          >
            <svg
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="3"
              stroke="currentColor"
              class="w-[14px] h-[14px] text-[#043f42ff]"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M4.5 12.75l6 6 9-13.5"
              />
            </svg>
            <div class="text-[14px]/[20px] box-border text-[#043f42ff] font-[Raleway,system-ui,sans-serif] font-bold text-left [white-space:nowrap]">
              Completed
            </div>
          </div>
          <button
            v-else-if="programme.students_completed"
            :disabled="programme.sponsor_dispatched_at || programme.sponsor_status === 'delivered' || isDispatchingSponsor"
            :class="(programme.sponsor_dispatched_at || programme.sponsor_status === 'delivered' || isDispatchingSponsor) ? 'box-border w-fit shrink-0 h-fit flex flex-row gap-[10px] p-[10px] justify-center items-center rounded-full transition-colors bg-[#d9d9d9ff] cursor-not-allowed opacity-50' : 'box-border w-fit shrink-0 h-fit flex flex-row gap-[10px] p-[10px] justify-center items-center rounded-full transition-colors bg-[#01d680ff] cursor-pointer hover:bg-[#01b56c]'"
            @click="!programme.sponsor_dispatched_at && programme.sponsor_status !== 'delivered' && !isDispatchingSponsor && dispatchSponsor()"
          >
            <svg
              v-if="isDispatchingSponsor"
              class="animate-spin -ml-1 mr-1 h-3.5 w-3.5 text-[#043f42ff] shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <div
              v-else
              class="box-border w-[11px] shrink-0 h-[11px] overflow-hidden relative"
            >
              <svg
                viewBox="0 -0.0000010728836059570312 447.98919677734375 480.83206284046173"
                preserveAspectRatio="none"
                xmlns="http://www.w3.org/2000/svg"
                class="box-border w-[9.625px] h-[10.33px] absolute left-[0.725px] top-[0.326px] overflow-visible [z-index:0]"
              >
                <path
                  d="M440.707 4.68907c-2.96576-2.46169-6.56988-4.03006-10.39261-4.52242-3.82272-0.49236-7.70666 0.11156-11.1994 1.74142l-419.11499 196.72399 0 38.066 176.037 70.414 112.916 173.72 38.07401 0 120.29999-455.4c0.97564-3.72947 0.87139-7.65942-0.30066-11.3319-1.17206-3.67249-3.36362-6.93629-6.31934-9.4111z m-137.18701 439.83598l-97.05699-149.32299 149.35301-163.582-23.63101-21.576-150.52201 164.857-145.14299-58.057 373.44301-175.29199-106.44302 402.97298z"
                  fill="#043f42ff"
                />
              </svg>
            </div>
            <div class="text-[14px]/[20px] box-border text-[#043f42ff] font-[Raleway,system-ui,sans-serif] font-bold text-left [white-space:nowrap]">
              {{ isDispatchingSponsor ? 'Sending...' : ((programme.sponsor_dispatched_at || programme.sponsor_status === 'delivered') ? 'Sent to sponsor' : 'Send to sponsor') }}
            </div>
          </button>
          <button
            v-else-if="trainerDone && !programme.students_completed"
            :disabled="programme.learners_dispatched_at || isDispatchingLearners"
            :class="(programme.learners_dispatched_at || isDispatchingLearners) ? 'box-border w-fit shrink-0 h-fit flex flex-row gap-[10px] p-[10px] justify-center items-center rounded-full transition-colors bg-[#d9d9d9ff] cursor-not-allowed opacity-50' : 'box-border w-fit shrink-0 h-fit flex flex-row gap-[10px] p-[10px] justify-center items-center rounded-full transition-colors bg-[#01d680ff] cursor-pointer hover:bg-[#01b56c]'"
            @click="!programme.learners_dispatched_at && !isDispatchingLearners && dispatchLearners()"
          >
            <svg
              v-if="isDispatchingLearners"
              class="animate-spin -ml-1 mr-1 h-3.5 w-3.5 text-[#043f42ff] shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <div
              v-else
              class="box-border w-[11px] shrink-0 h-[11px] overflow-hidden relative"
            >
              <svg
                viewBox="0 -0.0000010728836059570312 447.98919677734375 480.83206284046173"
                preserveAspectRatio="none"
                xmlns="http://www.w3.org/2000/svg"
                class="box-border w-[9.625px] h-[10.33px] absolute left-[0.725px] top-[0.326px] overflow-visible [z-index:0]"
              >
                <path
                  d="M440.707 4.68907c-2.96576-2.46169-6.56988-4.03006-10.39261-4.52242-3.82272-0.49236-7.70666 0.11156-11.1994 1.74142l-419.11499 196.72399 0 38.066 176.037 70.414 112.916 173.72 38.07401 0 120.29999-455.4c0.97564-3.72947 0.87139-7.65942-0.30066-11.3319-1.17206-3.67249-3.36362-6.93629-6.31934-9.4111z m-137.18701 439.83598l-97.05699-149.32299 149.35301-163.582-23.63101-21.576-150.52201 164.857-145.14299-58.057 373.44301-175.29199-106.44302 402.97298z"
                  fill="#043f42ff"
                />
              </svg>
            </div>
            <div class="text-[14px]/[20px] box-border text-[#043f42ff] font-[Raleway,system-ui,sans-serif] font-bold text-left [white-space:nowrap]">
              {{ isDispatchingLearners ? 'Sending...' : 'Send to Learners' }}
            </div>
          </button>
          <button
            v-else
            class="box-border w-fit shrink-0 h-fit flex flex-row gap-[10px] p-[10px] justify-center items-center rounded-full transition-colors"
            :class="localEnrollments.length === 0 ? 'bg-[#d9d9d9ff] cursor-not-allowed opacity-50 text-[#043f42ff]' : 'bg-[#01d680ff] cursor-pointer hover:bg-[#01b56c] text-[#043f42ff]'"
            @click="emitSendForSignatures"
          >
            <div class="box-border w-[11px] shrink-0 h-[11px] overflow-hidden relative">
              <svg
                viewBox="0 -0.0000010728836059570312 447.98919677734375 480.83206284046173"
                preserveAspectRatio="none"
                xmlns="http://www.w3.org/2000/svg"
                class="box-border w-[9.625px] h-[10.33px] absolute left-[0.725px] top-[0.326px] overflow-visible [z-index:0]"
              >
                <path
                  d="M440.707 4.68907c-2.96576-2.46169-6.56988-4.03006-10.39261-4.52242-3.82272-0.49236-7.70666 0.11156-11.1994 1.74142l-419.11499 196.72399 0 38.066 176.037 70.414 112.916 173.72 38.07401 0 120.29999-455.4c0.97564-3.72947 0.87139-7.65942-0.30066-11.3319-1.17206-3.67249-3.36362-6.93629-6.31934-9.4111z m-137.18701 439.83598l-97.05699-149.32299 149.35301-163.582-23.63101-21.576-150.52201 164.857-145.14299-58.057 373.44301-175.29199-106.44302 402.97298z"
                  fill="#000000ff"
                />
              </svg>
            </div>
            <div class="text-[14px]/[20px] box-border text-[#000000ff] font-[Raleway,system-ui,sans-serif] font-normal text-left [white-space:nowrap]">
              Sign as Training Provider
            </div>
          </button>

          <!-- 3-Dot Kebab Actions Menu (Export CSV, Download All, Email Template, Edit Mapping) -->
          <div
            class="relative shrink-0"
            @click.stop
          >
            <button
              id="programme-actions-kebab-btn"
              type="button"
              aria-haspopup="true"
              :aria-expanded="isActionsKebabOpen"
              class="box-border w-[40px] h-[40px] sm:w-[42px] sm:h-[42px] rounded-full border border-black/10 bg-white hover:bg-black/5 flex items-center justify-center text-[#043f42ff] transition-colors cursor-pointer p-0 focus:outline-none shadow-none"
              title="More actions"
              @click="isActionsKebabOpen = !isActionsKebabOpen"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <circle
                  cx="12"
                  cy="5"
                  r="2"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="2"
                />
                <circle
                  cx="12"
                  cy="19"
                  r="2"
                />
              </svg>
            </button>

            <!-- Kebab Context Menu Dropdown -->
            <div
              v-if="isActionsKebabOpen"
              class="absolute top-[48px] right-0 w-[220px] bg-white shadow-2xl rounded-[16px] z-50 flex flex-col p-1.5 border border-black/10 font-['Raleway',sans-serif]"
              @click.stop
            >
              <!-- 1. Export CSV -->
              <button
                type="button"
                class="flex items-center gap-2.5 px-3 py-2 text-[13px] rounded-[10px] font-semibold transition-colors text-left border-none bg-transparent w-full"
                :class="(!programme.cohort?.sponsor_completed_at || localEnrollments.length === 0) ? 'text-gray-400 cursor-not-allowed opacity-50' : 'text-[#043f42ff] hover:bg-[#edf7f2] cursor-pointer'"
                @click="if (programme.cohort?.sponsor_completed_at && localEnrollments.length > 0) { emitExport(); isActionsKebabOpen = false; }"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="shrink-0 text-[#043f42ff]"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line
                    x1="8"
                    y1="13"
                    x2="16"
                    y2="13"
                  />
                  <line
                    x1="8"
                    y1="17"
                    x2="16"
                    y2="17"
                  />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
                <span>Export CSV</span>
              </button>

              <!-- 2. Download All / ZIP -->
              <template v-if="freeTier">
                <subscription-gate class="w-full">
                  <button
                    type="button"
                    class="flex items-center gap-2.5 px-3 py-2 text-[13px] rounded-[10px] font-semibold transition-colors text-left border-none bg-transparent w-full"
                    :class="(!programme.cohort?.sponsor_completed_at || localEnrollments.length === 0) ? 'text-gray-400 cursor-not-allowed opacity-50' : 'text-[#043f42ff] hover:bg-[#edf7f2] cursor-pointer'"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="shrink-0 text-[#043f42ff]"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line
                        x1="12"
                        y1="15"
                        x2="12"
                        y2="3"
                      />
                    </svg>
                    <span>Download All</span>
                  </button>
                </subscription-gate>
              </template>
              <template v-else>
                <a
                  v-if="zipReadyUrl"
                  :href="zipReadyUrl"
                  download
                  class="flex items-center gap-2.5 px-3 py-2 text-[13px] text-[#01d680ff] hover:bg-[#edf7f2] rounded-[10px] font-semibold transition-colors no-underline cursor-pointer"
                  @click="resetZipState(); isActionsKebabOpen = false;"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="shrink-0"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line
                      x1="12"
                      y1="15"
                      x2="12"
                      y2="3"
                    />
                  </svg>
                  <span>Download Ready ZIP</span>
                </a>
                <button
                  v-else
                  type="button"
                  class="flex items-center gap-2.5 px-3 py-2 text-[13px] rounded-[10px] font-semibold transition-colors text-left border-none bg-transparent w-full"
                  :class="(!programme.cohort?.sponsor_completed_at || localEnrollments.length === 0) ? 'text-gray-400 cursor-not-allowed opacity-50' : (isCompilingZip ? 'text-[#01d680ff] cursor-wait bg-[#01d680ff]/10' : 'text-[#043f42ff] hover:bg-[#edf7f2] cursor-pointer')"
                  @click="if (programme.cohort?.sponsor_completed_at) { emitDownloadAll(); isActionsKebabOpen = false; }"
                >
                  <svg
                    v-if="isCompilingZip"
                    class="animate-spin h-4 w-4 text-[#043f42ff] shrink-0"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <svg
                    v-else
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="shrink-0 text-[#043f42ff]"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line
                      x1="12"
                      y1="15"
                      x2="12"
                      y2="3"
                    />
                  </svg>
                  <span>{{ isCompilingZip ? 'Compiling ZIP...' : 'Download All' }}</span>
                </button>
              </template>

              <!-- 3. Email Template -->
              <button
                type="button"
                class="flex items-center gap-2.5 px-3 py-2 text-[13px] text-[#043f42ff] hover:bg-[#edf7f2] rounded-[10px] font-semibold transition-colors text-left border-none bg-transparent w-full cursor-pointer"
                @click="openEmailModal(); isActionsKebabOpen = false;"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="shrink-0 text-[#043f42ff]"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                <span>Email Template</span>
              </button>

              <!-- 4. Edit Programme Mapping -->
              <button
                type="button"
                class="flex items-center gap-2.5 px-3 py-2 text-[13px] rounded-[10px] font-semibold transition-colors text-left border-none bg-transparent w-full"
                :class="isMappingDisabled ? 'text-gray-400 cursor-not-allowed opacity-50' : 'text-[#043f42ff] hover:bg-[#edf7f2] cursor-pointer'"
                :title="isMappingDisabled ? 'Mapping cannot be edited once signing has begun.' : 'Edit field mapping'"
                @click="if (!isMappingDisabled) { goToMapping(); isActionsKebabOpen = false; }"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="shrink-0 text-[#043f42ff]"
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                <span>Edit Programme Mapping</span>
              </button>
            </div>
          </div>

          <div class="box-border w-fit shrink-0 h-fit flex flex-row gap-[10px] sm:gap-[12px] justify-start items-center">
            <div class="box-border w-[215.951px] shrink-0 h-[42.634px] bg-[#ffffffff] rounded-full overflow-hidden relative">
              <div class="box-border w-fit h-fit absolute left-[16.683px] top-[12.049px] flex flex-row gap-[9.268px] justify-start items-center [z-index:0]">
                <div class="box-border w-[17.61px] shrink-0 h-[17.61px] relative">
                  <svg
                    viewbox="0 2.384185791015625e-7 18 17.99999976158142"
                    preserveaspectratio="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="box-border w-[13.207px] h-[13.207px] absolute left-[2.201px] top-[2.201px] overflow-visible [z-index:0]"
                  >
                    <path
                      d="M14 14l4 4m-18-10c0 2.12173 0.84285 4.15656 2.34315 5.65686 1.50029 1.50029 3.53512 2.34314 5.65685 2.34314 2.12173 0 4.15656-0.84285 5.65685-2.34314 1.50029-1.50029 2.34314-3.53512 2.34315-5.65686 0-2.12173-0.84285-4.15656-2.34315-5.65685-1.50029-1.50029-3.53512-2.34315-5.65685-2.34315-2.12173 0-4.15656 0.84285-5.65685 2.34315-1.50029 1.50029-2.34315 3.53512-2.34315 5.65685z"
                      fill="none"
                      stroke="#5b5c60ff"
                      stroke-width="1.101"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      vector-effect="non-scaling-stroke"
                    />
                  </svg>
                </div>
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="Search submissions"
                  class="text-[12.976px]/[19px] bg-transparent outline-none box-border w-[119px] shrink-0 h-[19px] text-[#5b5c60ff] font-[Raleway,system-ui,sans-serif] font-normal text-left"
                >
              </div>
            </div>
            <div
              class="box-border w-[42.634px] shrink-0 h-[42.634px] bg-[#ffffffff] rounded-full relative cursor-pointer"
              @click.stop="toggleSort"
            >
              <div class="box-border w-[25.024px] h-[25.024px] absolute left-[9.268px] top-[9.268px] overflow-hidden [z-index:0]">
                <svg
                  viewbox="0 -1.341104507446289e-7 18.5 17.57499898970127"
                  preserveaspectratio="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="box-border w-[19.29px] h-[18.325px] absolute left-[2.867px] top-[3.35px] overflow-visible [z-index:0]"
                >
                  <path
                    d="M18.5 8.787l-12.355 0m-4.361 0l-1.784 0m18.5 6.607l-5.748 0m-4.361 0l-8.391 0m18.5-13.214l-3.105 0m-4.361 0l-11.034 0m13.214 2.18c0.28628 0 0.56976-0.05639 0.83425-0.16594 0.26449-0.10956 0.50481-0.27013 0.70724-0.47257 0.20243-0.20243 0.36301-0.44275 0.47257-0.70724 0.10955-0.26449 0.16594-0.54797 0.16594-0.83425 0-0.28628-0.05639-0.56976-0.16594-0.83425-0.10956-0.26449-0.27013-0.50481-0.47257-0.70724-0.20243-0.20243-0.44275-0.36301-0.70724-0.47257-0.26449-0.10956-0.54797-0.16594-0.83425-0.16594-0.57817 0-1.13266 0.22968-1.54149 0.63851-0.40883 0.40883-0.63851 0.96332-0.63851 1.54149 0 0.57817 0.22968 1.13266 0.63851 1.54149 0.40883 0.40883 0.96332 0.63851 1.54149 0.63851z m-9.25 6.607c0.57817 0 1.13266-0.22968 1.54149-0.63851 0.40883-0.40883 0.63851-0.96332 0.63851-1.54149 0-0.57817-0.22968-1.13266-0.63851-1.54149-0.40883-0.40883-0.96332-0.63851-1.54149-0.63851-0.57817 0-1.13266 0.22968-1.54149 0.63851-0.40883 0.40883-0.63851 0.96332-0.63851 1.54149 0 0.57817 0.22968 1.13266 0.63851 1.54149 0.40883 0.40883 0.96332 0.63851 1.54149 0.63851z m6.607 6.608c0.5783 0 1.13292-0.22973 1.54185-0.63865 0.40892-0.40892 0.63915-0.96404 0.63915-1.54235 0-0.5783-0.23023-1.13242-0.63915-1.54135-0.40892-0.40892-0.96354-0.63865-1.54185-0.63865-0.57817 0-1.13266 0.22968-1.54149 0.63851-0.40883 0.40883-0.63851 0.96332-0.63851 1.54149 0 0.57817 0.22968 1.13267 0.63851 1.54149 0.40883 0.40883 0.96332 0.63951 1.54149 0.63951z"
                    fill="none"
                    stroke="#000000ff"
                    stroke-width="1.564"
                    stroke-linecap="round"
                    vector-effect="non-scaling-stroke"
                  />
                </svg>
              </div>
              <!-- Dropdown menu for sorting -->
              <div
                v-if="isSortOpen"
                class="absolute top-[48px] right-0 w-[220px] bg-white shadow-2xl rounded-[14px] z-50 flex flex-col py-2 border border-black/10"
                @click.stop
              >
                <div class="px-[16px] py-[8px] text-[12px] text-[#888888ff] uppercase tracking-wider font-bold">
                  Sort by Name
                </div>
                <div
                  class="px-[16px] py-[8px] text-[14px] hover:bg-[#f5f5f5ff] cursor-pointer flex justify-between items-center"
                  :class="currentSort === 'name-asc' ? 'text-[#01d680ff] font-semibold' : 'text-[#000000ff]'"
                  @click="setSort('name-asc')"
                >
                  Name (A - Z)
                  <span v-if="currentSort === 'name-asc'">✓</span>
                </div>
                <div
                  class="px-[16px] py-[8px] text-[14px] hover:bg-[#f5f5f5ff] cursor-pointer flex justify-between items-center"
                  :class="currentSort === 'name-desc' ? 'text-[#01d680ff] font-semibold' : 'text-[#000000ff]'"
                  @click="setSort('name-desc')"
                >
                  Name (Z - A)
                  <span v-if="currentSort === 'name-desc'">✓</span>
                </div>
                <div class="w-full h-[1px] bg-[#eef1f0ff] my-[4px]" />
                <div class="px-[16px] py-[8px] text-[12px] text-[#888888ff] uppercase tracking-wider font-bold">
                  Sort by Date
                </div>
                <div
                  class="px-[16px] py-[8px] text-[14px] hover:bg-[#f5f5f5ff] cursor-pointer flex justify-between items-center"
                  :class="currentSort === 'date-desc' ? 'text-[#01d680ff] font-semibold' : 'text-[#000000ff]'"
                  @click="setSort('date-desc')"
                >
                  Last Updated (Newest)
                  <span v-if="currentSort === 'date-desc'">✓</span>
                </div>
                <div
                  class="px-[16px] py-[8px] text-[14px] hover:bg-[#f5f5f5ff] cursor-pointer flex justify-between items-center"
                  :class="currentSort === 'date-asc' ? 'text-[#01d680ff] font-semibold' : 'text-[#000000ff]'"
                  @click="setSort('date-asc')"
                >
                  Last Updated (Oldest)
                  <span v-if="currentSort === 'date-asc'">✓</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="hidden lg:flex box-border w-full min-h-0 flex-1 flex-row gap-4 lg:gap-5 justify-start items-start">
        <div class="box-border flex-1 min-w-0 h-full flex flex-col gap-3 justify-start items-end">
          <div class="box-border w-full min-h-0 flex-1 flex flex-col bg-white rounded-[14px] overflow-hidden relative border border-black/[0.08] shadow-none">
            <div class="box-border w-full h-[50px] shrink-0 flex flex-row items-center px-[20px] pt-[15px] border-b border-[#eef1f0ff]">
              <div class="flex-[1.5] min-w-0 text-[14px]/[19px] box-border text-[#000000ff] font-[Raleway,system-ui,sans-serif] font-bold text-left [white-space:nowrap] px-[5px]">
                Email
              </div>
              <div class="flex-[1.5] min-w-0 text-[14px]/[19px] box-border text-[#000000ff] font-[Raleway,system-ui,sans-serif] font-bold text-left [white-space:nowrap] px-[5px]">
                Learner
              </div>
              <div class="flex-[1.2] min-w-0 text-[14px]/[19px] box-border text-[#000000ff] font-[Raleway,system-ui,sans-serif] font-bold text-left [white-space:nowrap] px-[5px]">
                Status
              </div>
              <div class="flex-[1.2] min-w-0 text-[14px]/[19px] box-border text-[#000000ff] font-[Raleway,system-ui,sans-serif] font-bold text-left [white-space:nowrap] px-[5px]">
                Date
              </div>
              <div class="flex-[1] min-w-0 text-[14px]/[19px] box-border text-[#000000ff] font-[Raleway,system-ui,sans-serif] font-bold text-left [white-space:nowrap] px-[5px]">
                Attachments
              </div>
              <div class="flex-[1] min-w-0 text-[14px]/[19px] box-border text-[#000000ff] font-[Raleway,system-ui,sans-serif] font-bold text-center [white-space:nowrap] px-[5px]">
                Action
              </div>
            </div>
            <div class="box-border w-full min-h-0 flex-1 overflow-y-auto flex flex-col gap-[8px] p-[8px] px-[15px]">
              <StudentRow
                v-for="enrollment in paginatedEnrollments"
                :key="enrollment.id"
                :enrollment="enrollment"
              />
              <div
                v-if="localEnrollments.length === 0"
                class="flex flex-col items-center justify-center w-full h-[400px] gap-[10px] text-[#5b5c60ff] font-[Raleway,system-ui,sans-serif]"
              >
                <div class="text-[18px] font-bold text-[#000000ff]">
                  No students added yet.
                </div>
                <div class="text-[14px]">
                  Click <span class="font-bold text-[#000000ff]">Edit</span> to map students.
                </div>
              </div>
              <div
                v-else-if="filteredEnrollments.length === 0"
                class="text-center w-full py-8 text-[#5b5c60ff] font-[Raleway,system-ui,sans-serif]"
              >
                No submissions found
              </div>
            </div>
          </div>
          <div class="box-border w-fit h-fit shrink-0 flex flex-row gap-[11px] justify-start items-center">
            <div class="text-[12.976px]/[19px] box-border text-[#000000ff] font-[Raleway,system-ui,sans-serif] font-normal text-left [white-space:nowrap]">
              {{ filteredEnrollments.length > 0 ? startIndex + 1 : 0 }}-{{ endIndex }} of {{ filteredEnrollments.length }}
            </div>
            <div class="box-border w-fit shrink-0 h-fit flex flex-row gap-[10px] justify-start items-center">
              <div
                class="box-border w-[24px] shrink-0 h-[24px] overflow-hidden relative"
                :class="{'opacity-50 cursor-not-allowed': currentPage === 1, 'cursor-pointer hover:opacity-75': currentPage > 1}"
                @click="prevPage"
              >
                <svg
                  viewbox="3.725290298461914e-9 0.0000031441450119018555 3.9885363541543484 6.982922747731209"
                  preserveaspectratio="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="box-border w-[3.989px] h-[6.983px] absolute left-[10.003px] top-[8.509px] overflow-visible [z-index:0]"
                >
                  <path
                    d="M0.14707 3.84146c-0.04656-0.04562-0.08356-0.10008-0.10881-0.16017-0.02525-0.0601-0.03826-0.12463-0.03826-0.18983 0-0.06519 0.01301-0.12972 0.03826-0.18982 0.02525-0.0601 0.06225-0.11456 0.10881-0.16018l3-3c0.09314-0.09128 0.21855-0.14212 0.34897-0.14145 0.13041 0.00066 0.2553 0.05276 0.34751 0.14497 0.09222 0.09222 0.14432 0.2171 0.14498 0.34752 0.00066 0.13041-0.05018 0.25582-0.14146 0.34896l-2.64 2.65 2.64 2.65c0.09128 0.09314 0.14212 0.21855 0.14146 0.34897-0.00066 0.13041-0.05276 0.2553-0.14498 0.34752-0.09222 0.09222-0.2171 0.14432-0.34751 0.14497-0.13041 0.00066-0.25582-0.05018-0.34897-0.14146l-3-3z"
                    fill="#000000ff"
                  />
                </svg>
                <svg
                  viewbox="-0.0000010728836059570312 -0.000005498528480529785 19.86601173877716 19.866016164422035"
                  preserveaspectratio="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="box-border w-[19.866px] h-[19.866px] absolute left-[2.067px] top-[2.067px] overflow-visible [z-index:1]"
                >
                  <path
                    d="M9.933 19.86601c-1.96456 0-3.88501-0.58256-5.51847-1.67402-1.63347-1.09145-2.90661-2.64277-3.65842-4.45779-0.75181-1.81502-0.94851-3.81221-0.56525-5.73903 0.38327-1.92681 1.32929-3.6967 2.71845-5.08586 1.38916-1.38916 3.15905-2.33518 5.08586-2.71845 1.92681-0.38327 3.92401-0.18656 5.73903 0.56525 1.81502 0.75181 3.36634 2.02494 4.45779 3.65842 1.09145 1.63347 1.67401 3.55392 1.67402 5.51847-0.00291 2.6335-1.05036 5.15831-2.91253 7.02048-1.86217 1.86217-4.38697 2.90961-7.02048 2.91253z m0-18.86601c-1.76678 0-3.49388 0.52391-4.9629 1.50549-1.46902 0.98157-2.61399 2.37671-3.29011 4.009-0.67612 1.63229-0.85302 3.42842-0.50834 5.16126 0.34468 1.73283 1.19547 3.32454 2.44477 4.57384 1.2493 1.2493 2.84101 2.10009 4.57384 2.44477 1.73283 0.34468 3.52896 0.16778 5.16126-0.50834 1.63229-0.67612 3.02744-1.82108 4.009-3.29011 0.98157-1.46903 1.50548-3.19613 1.50549-4.96291-0.00265-2.36837-0.94465-4.63897-2.61934-6.31366-1.67469-1.67469-3.9453-2.61669-6.31367-2.61934z"
                    fill="#000000ff"
                  />
                </svg>
              </div>
              <div
                class="box-border relative w-[24px] h-[24px] shrink-0"
                :class="{'opacity-50 cursor-not-allowed': currentPage === totalPages || totalPages === 0, 'cursor-pointer hover:opacity-75': currentPage < totalPages}"
                @click="nextPage"
              >
                <div class="box-border w-[24px] h-[24px] [transform:scaleX(-1)] [transform-origin:top_left] absolute left-[24px] top-0 overflow-hidden">
                  <svg
                    viewbox="3.725290298461914e-9 0.0000031441450119018555 3.9885363541543484 6.982922747731209"
                    preserveaspectratio="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="box-border w-[3.989px] h-[6.983px] absolute left-[10.003px] top-[8.509px] overflow-visible [z-index:0]"
                  >
                    <path
                      d="M0.14707 3.84146c-0.04656-0.04562-0.08356-0.10008-0.10881-0.16017-0.02525-0.0601-0.03826-0.12463-0.03826-0.18983 0-0.06519 0.01301-0.12972 0.03826-0.18982 0.02525-0.0601 0.06225-0.11456 0.10881-0.16018l3-3c0.09314-0.09128 0.21855-0.14212 0.34897-0.14145 0.13041 0.00066 0.2553 0.05276 0.34751 0.14497 0.09222 0.09222 0.14432 0.2171 0.14498 0.34752 0.00066 0.13041-0.05018 0.25582-0.14146 0.34896l-2.64 2.65 2.64 2.65c0.09128 0.09314 0.14212 0.21855 0.14146 0.34897-0.00066 0.13041-0.05276 0.2553-0.14498 0.34752-0.09222 0.09222-0.2171 0.14432-0.34751 0.14497-0.13041 0.00066-0.25582-0.05018-0.34897-0.14146l-3-3z"
                      fill="#000000ff"
                    />
                  </svg>
                  <svg
                    viewbox="-0.0000010728836059570312 -0.000005498528480529785 19.86601173877716 19.866016164422035"
                    preserveaspectratio="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="box-border w-[19.866px] h-[19.866px] absolute left-[2.067px] top-[2.067px] overflow-visible [z-index:1]"
                  >
                    <path
                      d="M9.933 19.86601c-1.96456 0-3.88501-0.58256-5.51847-1.67402-1.63347-1.09145-2.90661-2.64277-3.65842-4.45779-0.75181-1.81502-0.94851-3.81221-0.56525-5.73903 0.38327-1.92681 1.32929-3.6967 2.71845-5.08586 1.38916-1.38916 3.15905-2.33518 5.08586-2.71845 1.92681-0.38327 3.92401-0.18656 5.73903 0.56525 1.81502 0.75181 3.36634 2.02494 4.45779 3.65842 1.09145 1.63347 1.67401 3.55392 1.67402 5.51847-0.00291 2.6335-1.05036 5.15831-2.91253 7.02048-1.86217 1.86217-4.38697 2.90961-7.02048 2.91253z m0-18.86601c-1.76678 0-3.49388 0.52391-4.9629 1.50549-1.46902 0.98157-2.61399 2.37671-3.29011 4.009-0.67612 1.63229-0.85302 3.42842-0.50834 5.16126 0.34468 1.73283 1.19547 3.32454 2.44477 4.57384 1.2493 1.2493 2.84101 2.10009 4.57384 2.44477 1.73283 0.34468 3.52896 0.16778 5.16126-0.50834 1.63229-0.67612 3.02744-1.82108 4.009-3.29011 0.98157-1.46903 1.50548-3.19613 1.50549-4.96291-0.00265-2.36837-0.94465-4.63897-2.61934-6.31366-1.67469-1.67469-3.9453-2.61669-6.31367-2.61934z"
                      fill="#000000ff"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="box-border w-[240px] xl:w-[260px] shrink-0">
          <SignatureProgressPanel
            :programme="localProgramme"
            :enrollments="localEnrollments"
          />
        </div>
      </div>

      <!-- Mobile & Tablet Cards View (Loads 10 at a time with Load more button) -->
      <div
        ref="mobileCardsContainerRef"
        class="flex lg:hidden flex-col w-full flex-1 min-h-0 overflow-y-auto hide-scrollbar pb-28 gap-3"
      >
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 w-full">
          <StudentCard
            v-for="enrollment in visibleMobileEnrollments"
            :key="enrollment.id"
            :enrollment="enrollment"
          />
        </div>

        <!-- Empty states -->
        <div
          v-if="localEnrollments.length === 0"
          class="flex flex-col items-center justify-center w-full py-16 gap-2 text-[#5b5c60] font-[Raleway,sans-serif]"
        >
          <div class="text-[16px] font-bold text-[#043f42]">
            No students added yet.
          </div>
          <div class="text-[13px]">
            Click <span class="font-bold text-[#043f42]">Edit</span> to map students.
          </div>
        </div>
        <div
          v-else-if="filteredEnrollments.length === 0"
          class="text-center w-full py-12 text-[#5b5c60] font-[Raleway,sans-serif] text-[14px]"
        >
          No submissions found
        </div>

        <!-- Mobile Pagination: Load More Button -->
        <div
          v-if="filteredEnrollments.length > 0"
          class="w-full py-4 flex flex-col items-center justify-center gap-2"
        >
          <button
            v-if="visibleMobileEnrollments.length < filteredEnrollments.length"
            id="mobile_load_more_button"
            type="button"
            class="px-6 py-2.5 rounded-full bg-white border border-[#043f4226] hover:border-[#043f42] text-[#043f42] hover:bg-gray-50 active:scale-95 text-xs font-bold shadow-xs transition-all flex items-center gap-2 cursor-pointer"
            @click="loadMoreMobileSubmissions"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
            <span>Load more</span>
            <span class="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#043f4210] text-[#043f42]">
              +{{ Math.min(10, filteredEnrollments.length - visibleMobileEnrollments.length) }}
            </span>
          </button>
          <div
            v-else-if="filteredEnrollments.length > 10"
            class="text-xs text-[#5b5c60] font-medium"
          >
            All {{ filteredEnrollments.length }} submissions loaded
          </div>
        </div>
      </div>

      <!-- Mobile Right FAB: Primary Action Circular Button with Context Menu -->
      <div
        class="fixed bottom-[74px] right-4 md:bottom-[82px] md:right-8 z-30 lg:hidden"
        @click.stop
      >
        <!-- Action Context Menu (Popup above FAB) -->
        <div
          v-if="isActionMenuOpen"
          class="absolute bottom-full right-0 mb-3 w-64 bg-white rounded-[18px] shadow-2xl border border-black/10 p-4 z-40 flex flex-col gap-3 font-['Raleway',sans-serif] animate-in fade-in slide-in-from-bottom-2 duration-150"
        >
          <div class="flex items-center justify-between gap-2">
            <div class="flex items-center gap-2">
              <div class="w-7 h-7 rounded-full bg-[#01d680]/20 text-[#043f42] flex items-center justify-center shrink-0">
                <svg
                  v-if="currentActionState.icon === 'check'"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="3"
                  stroke="currentColor"
                  class="w-4 h-4"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M4.5 12.75l6 6 9-13.5"
                  />
                </svg>
                <svg
                  v-else-if="currentActionState.icon === 'send'"
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <line
                    x1="22"
                    y1="2"
                    x2="11"
                    y2="13"
                  />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
                <svg
                  v-else
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                </svg>
              </div>
              <h4 class="text-[13px] font-bold text-[#043f42]">
                {{ currentActionState.title }}
              </h4>
            </div>
            <button
              type="button"
              class="w-6 h-6 rounded-full flex items-center justify-center text-gray-400 hover:text-black hover:bg-gray-100 cursor-pointer transition-colors"
              aria-label="Close action menu"
              @click="closeActionMenu"
            >
              ✕
            </button>
          </div>

          <p class="text-[12px] text-[#5b5c60] leading-snug">
            {{ currentActionState.description }}
          </p>

          <button
            v-if="currentActionState.actionText"
            type="button"
            :disabled="currentActionState.disabled"
            class="w-full py-2.5 px-3 bg-[#01d680] hover:bg-[#01b56c] active:scale-95 text-[#043f42] rounded-full font-bold text-xs flex items-center justify-center gap-2 cursor-pointer shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            @click="executeFabAction"
          >
            <svg
              v-if="currentActionState.isPending"
              class="animate-spin h-3.5 w-3.5 text-[#043f42]"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>{{ currentActionState.actionText }}</span>
          </button>
        </div>

        <!-- Solid, Opaque Circular Button Trigger -->
        <button
          type="button"
          class="w-12 h-12 md:w-14 md:h-14 bg-[#01d680] text-[#043f42] rounded-full shadow-xl flex items-center justify-center cursor-pointer transition-all active:scale-95 focus:outline-none select-none"
          :title="currentActionState.title"
          :aria-label="currentActionState.title"
          @click="toggleActionMenu"
        >
          <!-- Spinning state -->
          <svg
            v-if="currentActionState.isPending"
            class="animate-spin h-5 w-5 text-[#043f42]"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>

          <!-- Completed: Checkmark -->
          <svg
            v-else-if="currentActionState.icon === 'check'"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="3.5"
            stroke="currentColor"
            class="w-6 h-6 md:w-7 md:h-7 text-[#043f42]"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M4.5 12.75l6 6 9-13.5"
            />
          </svg>

          <!-- Send (Learner or Sponsor): Paper Plane -->
          <svg
            v-else-if="currentActionState.icon === 'send'"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="w-5 h-5 md:w-6 md:h-6 text-[#043f42] -ml-0.5"
          >
            <line
              x1="22"
              y1="2"
              x2="11"
              y2="13"
            />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>

          <!-- Sign (Trainer): Signature Pen -->
          <svg
            v-else
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="w-5 h-5 md:w-6 md:h-6 text-[#043f42]"
          >
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
          </svg>
        </button>
      </div>

      <!-- Mobile Left FAB: Signature Progress Circular Button -->
      <div class="fixed bottom-[74px] left-4 md:bottom-[82px] md:left-8 z-30 lg:hidden">
        <button
          type="button"
          class="relative w-12 h-12 md:w-14 md:h-14 rounded-full bg-white shadow-2xl border border-black/10 flex items-center justify-center cursor-pointer active:scale-95 transition-all focus:outline-none"
          title="Signature Progress"
          aria-label="View Signature Progress"
          @click="openProgressModal"
        >
          <!-- SVG Circular Progress Ring -->
          <svg
            class="w-12 h-12 md:w-14 md:h-14 -rotate-90 transform"
            viewBox="0 0 48 48"
          >
            <circle
              cx="24"
              cy="24"
              r="20"
              stroke="#e5e7eb"
              stroke-width="2.5"
              fill="transparent"
            />
            <circle
              cx="24"
              cy="24"
              r="20"
              :stroke="progressStrokeColor"
              stroke-width="2.5"
              stroke-linecap="round"
              fill="transparent"
              stroke-dasharray="125.66"
              :stroke-dashoffset="progressStrokeOffset"
              class="transition-all duration-500 ease-out"
            />
          </svg>

          <!-- Center Percentage Number -->
          <span class="absolute inset-0 flex items-center justify-center text-[11px] md:text-[12px] font-semibold text-[#043f42] font-['Raleway',sans-serif]">
            {{ overallProgressPercent }}%
          </span>
        </button>
      </div>

      <!-- Signature Progress Context Modal (Popup) -->
      <Teleport to="body">
        <div
          v-if="isProgressModalOpen"
          class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-xs p-3 sm:p-4 font-['Raleway',sans-serif]"
          @click.self="closeProgressModal"
        >
          <div class="bg-white rounded-[24px] max-w-md w-full p-4 sm:p-5 shadow-2xl relative animate-in fade-in slide-in-from-bottom-6 duration-200 border border-black/10 max-h-[85vh] overflow-y-auto flex flex-col gap-3">
            <div class="flex items-center justify-between pb-3 border-b border-gray-100">
              <h3 class="text-[17px] font-bold text-[#043f42] whitespace-nowrap">
                Signature Progress
              </h3>
              <button
                type="button"
                class="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-black hover:bg-gray-100 cursor-pointer transition-colors"
                aria-label="Close"
                @click="closeProgressModal"
              >
                ✕
              </button>
            </div>

            <!-- Signature Progress Panel inside Modal (internal title hidden to avoid duplication) -->
            <SignatureProgressPanel
              :programme="localProgramme"
              :enrollments="localEnrollments"
              :hide-title="true"
              class="border-0 p-0 sm:p-0 shadow-none bg-transparent"
            />
          </div>
        </div>
      </Teleport>
    </div>
  </div>
  <!-- Delete Modal -->
  <dialog
    class="modal"
    :class="{'modal-open': showDeleteModal}"
  >
    <div class="modal-box w-[calc(100%-32px)] max-w-md mx-4 sm:mx-auto font-['Raleway'] rounded-[20px] border border-[#22ec9b] p-5 sm:p-7 shadow-2xl bg-white">
      <h3 class="font-bold text-[18px] text-[#043f42] mb-1">
        Remove Student
      </h3>
      <p class="text-[13px] text-gray-500 mb-4">
        This action cannot be undone.
      </p>
      <div class="space-y-3 text-[14px]">
        <p class="text-[#043f42]">
          Are you sure you want to remove <strong>{{ studentToDelete?.name }} {{ studentToDelete?.surname }}</strong>?
        </p>

        <div class="bg-gray-50 rounded-[12px] p-3 text-[13px] space-y-1 border border-gray-100">
          <p><span class="text-gray-500">Email:</span> <span class="font-medium text-[#043f42]">{{ studentToDelete?.student_email }}</span></p>
          <p><span class="text-gray-500">Status:</span> <span class="font-medium text-[#043f42]">{{ studentToDelete?.submission?.state || 'Draft' }}</span></p>
        </div>

        <div class="form-control mt-3">
          <label class="label pb-1">
            <span class="label-text text-[13px] font-semibold text-[#043f42]">Reason for removal (Required)</span>
          </label>
          <textarea
            v-model="deleteReason"
            class="textarea textarea-bordered h-20 text-[13px] rounded-[10px] focus:border-[#01d680] focus:ring-1 focus:ring-[#01d680]"
            placeholder="Enter reason for removal..."
            maxlength="500"
          />
          <label
            v-if="deleteError"
            class="label pt-1"
          >
            <span class="label-text-alt text-error text-[12px]">{{ deleteError }}</span>
          </label>
        </div>
      </div>
      <div class="modal-action mt-5">
        <button
          class="btn rounded-full px-5 py-2 text-sm font-semibold border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
          :disabled="isDeleting"
          @click="closeDeleteModal"
        >
          Cancel
        </button>
        <button
          class="btn btn-error text-white rounded-full px-5 py-2 text-sm font-semibold"
          :disabled="isDeleting"
          @click="deleteStudent"
        >
          <svg
            v-if="isDeleting"
            class="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Remove Student
        </button>
      </div>
    </div>
    <form
      method="dialog"
      class="modal-backdrop"
    >
      <button
        :disabled="isDeleting"
        @click.prevent="!isDeleting && (closeDeleteModal())"
      >
        close
      </button>
    </form>
  </dialog>

  <div
    v-if="showToast"
    class="toast toast-top toast-center z-[9999]"
  >
    <div
      class="alert text-white shadow-lg"
      :class="{
        'alert-success bg-[#01d680ff] border-[#01d680ff] text-[#043f42ff]': toastType === 'success',
        'alert-error bg-red-500 border-red-500': toastType === 'error',
        'alert-info bg-blue-500 border-blue-500': toastType === 'info'
      }"
    >
      <span class="font-bold">{{ toastMessage }}</span>
    </div>
  </div>

  <!-- Floating Progress Bar (Card & FAB) -->
  <div
    v-if="isCompilingZip"
    class="fixed bottom-[32px] right-[32px] z-[9998] flex items-end justify-end"
  >
    <!-- Expanded Card -->
    <transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 scale-95 translate-y-4"
      enter-to-class="opacity-100 scale-100 translate-y-0"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 scale-100 translate-y-0"
      leave-to-class="opacity-0 scale-95 translate-y-4"
    >
      <div
        v-if="!zipProgressMinimized"
        class="w-[340px] bg-white rounded-[20px] shadow-2xl border border-[#e2e8f0] p-[20px] flex flex-col gap-[12px] relative"
      >
        <!-- Minimize Button -->
        <button
          class="absolute top-[12px] right-[12px] text-[#a0aec0] hover:text-[#4a5568] transition-colors rounded-full p-1 hover:bg-[#edf2f7]"
          @click="zipProgressMinimized = true"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          ><line
            x1="5"
            y1="12"
            x2="19"
            y2="12"
          /></svg>
        </button>

        <div class="flex items-center justify-between pr-[24px]">
          <div class="flex items-center gap-[10px]">
            <svg
              class="animate-spin h-4 w-4 text-[#01d680ff]"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span class="font-['Raleway'] font-bold text-[#043f42ff] text-[15px]">Compiling Master ZIP</span>
          </div>
          <div class="text-[#718096] text-[13px] font-['Raleway'] font-semibold">
            {{ zipProgress }} / {{ zipTotal || programme.students_count }}
          </div>
        </div>

        <div class="w-full h-[6px] bg-[#f4f4f4] rounded-full overflow-hidden mt-1">
          <div
            class="h-full bg-[#01d680ff] transition-all duration-300 ease-out rounded-full"
            :style="{ width: zipTotal ? `${(zipProgress / zipTotal) * 100}%` : '0%' }"
          />
        </div>

        <div class="text-[#718096] text-[13px] font-['Raleway'] truncate">
          <span v-if="zipCurrentName">Processing: <span class="font-semibold text-[#4a5568]">{{ zipCurrentName }}</span></span>
          <span v-else>Initializing...</span>
        </div>
      </div>
    </transition>

    <!-- Minimized FAB -->
    <transition
      enter-active-class="transition-all duration-300 ease-out delay-100"
      enter-from-class="opacity-0 scale-50"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-50"
    >
      <button
        v-if="zipProgressMinimized"
        class="absolute bottom-0 right-0 w-[56px] h-[56px] bg-white rounded-full shadow-[0_8px_30px_rgb(0,0,0,0.15)] flex items-center justify-center hover:scale-105 transition-transform"
        @click="zipProgressMinimized = false"
      >
        <!-- Circular Progress -->
        <svg
          class="absolute inset-0 w-full h-full -rotate-90 transform"
          viewBox="0 0 56 56"
        >
          <!-- Background circle -->
          <circle
            cx="28"
            cy="28"
            r="26"
            fill="none"
            stroke="#f4f4f4"
            stroke-width="4"
          />
          <!-- Progress circle -->
          <circle
            cx="28"
            cy="28"
            r="26"
            fill="none"
            stroke="#01d680ff"
            stroke-width="4"
            stroke-linecap="round"
            :stroke-dasharray="2 * Math.PI * 26"
            :stroke-dashoffset="zipTotal ? (2 * Math.PI * 26) * (1 - (zipProgress / zipTotal)) : (2 * Math.PI * 26)"
            class="transition-all duration-300 ease-out"
          />
        </svg>

        <!-- Download Icon in center -->
        <svg
          class="text-[#043f42ff] z-10 animate-pulse"
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line
            x1="12"
            y1="15"
            x2="12"
            y2="3"
          />
        </svg>
      </button>
    </transition>
  </div>

  <!-- Email Template Customization Modal -->
  <Teleport to="body">
    <EmailTemplateModal
      :is-open="isEmailModalOpen"
      :programme="props.programme"
      @close="isEmailModalOpen = false"
      @saved="handleEmailTemplatesSaved"
    />
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import StudentRow from './StudentRow.vue'
import StudentCard from './StudentCard.vue'
import SignatureProgressPanel from './SignatureProgressPanel.vue'
import EmailTemplateModal from './EmailTemplateModal.vue'

const props = defineProps({
  programme: {
    type: Object,
    required: true
  },
  enrollments: {
    type: Array,
    default: () => []
  },
  freeTier: {
    type: Boolean,
    default: false
  },
  zipExportInProgress: {
    type: Boolean,
    default: false
  },
  accountId: {
    type: [String, Number],
    default: null
  }
})

defineEmits(['programme-action'])

const localProgramme = ref({ ...props.programme })
watch(() => props.programme, (newVal) => {
  if (newVal) {
    Object.assign(localProgramme.value, newVal)
  }
}, { deep: true })

const isEmailModalOpen = ref(false)
const isActionsKebabOpen = ref(false)
const isMobileActionsKebabOpen = ref(false)
const isDispatchingLearners = ref(false)
const isDispatchingSponsor = ref(false)

const openEmailModal = () => {
  isEmailModalOpen.value = true
}

const handleEmailTemplatesSaved = (data) => {
  displayToast('Email templates saved successfully!')
  if (props.programme?.template?.preferences) {
    props.programme.template.preferences.email_templates = data.email_templates
  }
}

const isEditingName = ref(false)
const editName = ref('')
const nameInput = ref(null)

const startEditingName = () => {
  editName.value = props.programme.name || 'Programme #' + props.programme.id
  isEditingName.value = true
  setTimeout(() => {
    nameInput.value?.focus()
  }, 50)
}

const cancelEditName = () => {
  isEditingName.value = false
}

const saveName = async () => {
  if (!isEditingName.value) return
  isEditingName.value = false

  if (editName.value.trim() !== '' && editName.value !== props.programme.name) {
    const oldName = props.programme.name
    props.programme.name = editName.value.trim()
    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute('content')
      const cohortId = props.programme.cohort_id || props.programme.cohort?.id
      const programmeId = props.programme.slug || props.programme.id

      await fetch(`/cohorts/${cohortId}/programmes/${programmeId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ programme: { name: editName.value.trim() } })
      })
      displayToast('Programme name updated', 'success')
    } catch (e) {
      props.programme.name = oldName
      displayToast('Failed to update programme name', 'error')
    }
  }
}

defineExpose({
  startEditingName,
  cancelEditName,
  saveName,
  isEditingName,
  editName
})

const isCompilingZip = ref(props.zipExportInProgress)
const zipProgress = ref(0)
const zipTotal = ref(0)
const zipCurrentName = ref('')
const zipProgressMinimized = ref(false)
const zipReadyUrl = ref('')

const emitDownloadAll = async () => {
  if ((localEnrollments.value || []).length === 0 || !props.programme.students_completed) return
  if (isCompilingZip.value) return

  isCompilingZip.value = true
  zipReadyUrl.value = ''

  try {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute('content')
    const cohortIdentifier = props.programme.cohort?.slug || props.programme.cohort_id || props.programme.cohort?.id
    const progIdentifier = props.programme.slug || props.programme.id

    const res = await fetch(`/cohorts/${cohortIdentifier}/programmes/${progIdentifier}/bulk_download`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Content-Type': 'application/json'
      }
    })

    if (res.status === 409) {
      displayToast('A bulk download is already in progress for your account.', 'error')
      isCompilingZip.value = true
    } else if (!res.ok) {
      throw new Error('Failed to start bulk download')
    } else {
      displayToast('Started compiling ZIP. This may take a few minutes.', 'info')
    }
  } catch (err) {
    console.error(err)
    displayToast('Failed to start bulk download.', 'error')
    isCompilingZip.value = false
  }
}

const resetZipState = () => {
  setTimeout(() => {
    zipReadyUrl.value = ''
  }, 1000)
}

const emitExport = () => {
  if ((localEnrollments.value || []).length > 0 && props.programme.students_completed) {
    const cohortId = props.programme.cohort_id || props.programme.cohort?.id
    const programmeId = props.programme.slug || props.programme.id
    window.location.href = `/cohorts/${cohortId}/programmes/${programmeId}/export.xlsx`
  }
}

const isMappingDisabled = computed(() => {
  return !!(
    props.programme.mapping_locked ||
    props.programme.has_started_signing ||
    props.programme.trainer_completed ||
    trainerDone.value ||
    totalSignaturesCompleted.value > 0
  )
})

const goToMapping = () => {
  if (isMappingDisabled.value) return
  const cohortId = props.programme.cohort_id || props.programme.cohort?.id
  if (!cohortId) return
  const programmeId = props.programme.slug || props.programme.id
  window.location.href = `/cohorts/${cohortId}/programmes/${programmeId}/mapping`
}

const deletedIds = ref(new Set())
const localEnrollments = ref(props.enrollments ? [...props.enrollments] : [])
watch(() => props.enrollments, (newVal) => {
  const all = newVal ? [...newVal] : []
  localEnrollments.value = all.filter(e => !deletedIds.value.has(e.id))
})

const showDeleteModal = ref(false)
const studentToDelete = ref(null)
const deleteReason = ref('')
const deleteError = ref('')

const closeDeleteModal = () => {
  showDeleteModal.value = false
  deleteError.value = ''
  deleteReason.value = ''
}
const isDeleting = ref(false)
const showToast = ref(false)
const toastMessage = ref('')
const toastType = ref('success')
let toastTimeout = null

const displayToast = (msg, type = 'success') => {
  toastMessage.value = msg
  toastType.value = type
  showToast.value = true
  if (toastTimeout) clearTimeout(toastTimeout)
  toastTimeout = setTimeout(() => { showToast.value = false }, 4000)
}

const handleProgrammeAction = async (event) => {
  if (event.detail?.action === 'delete') {
    const id = event.detail.enrollment_id
    const enrollment = localEnrollments.value.find(e => e.id === id)
    if (enrollment) {
      studentToDelete.value = enrollment
      deleteReason.value = ''
      deleteError.value = ''
      showDeleteModal.value = true
    }
  } else if (event.detail?.action === 'zip_progress') {
    zipProgress.value = event.detail.current
    zipTotal.value = event.detail.total
    zipCurrentName.value = event.detail.name
    isCompilingZip.value = true
  } else if (event.detail?.action === 'zip_ready') {
    zipProgress.value = 0
    zipTotal.value = 0
    zipCurrentName.value = ''
    zipReadyUrl.value = event.detail.url
    isCompilingZip.value = false
    displayToast('Your bulk download is ready!', 'success')

    if (event.detail.url) {
      const now = Date.now()
      if (window._doccuLastDownloadedZipUrl === event.detail.url && (now - (window._doccuLastDownloadedZipTime || 0)) < 5000) {
        return
      }
      window._doccuLastDownloadedZipUrl = event.detail.url
      window._doccuLastDownloadedZipTime = now

      try {
        const a = document.createElement('a')
        a.href = event.detail.url
        a.setAttribute('download', '')
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      } catch (e) {
        console.warn('Auto download trigger skipped:', e)
      }
    }
  } else if (event.detail?.action === 'download') {
    const id = event.detail.enrollment_id
    const cohortIdentifier = props.programme.cohort?.slug || props.programme.cohort_id || props.programme.cohort?.id
    const progIdentifier = props.programme.slug || props.programme.id
    window.location.href = `/cohorts/${cohortIdentifier}/programmes/${progIdentifier}/enrollments/${id}/download`
  } else if (event.detail?.action === 'view_submission') {
    const submitterId = event.detail.submitter_id
    if (submitterId) {
      const order = filteredEnrollments.value.map(e => {
        const submission = e.submission
        if (!submission || !submission.submitters) return null
        const s = submission.submitters.find(sub => sub.email === e.student_email || sub.uuid?.toLowerCase() === 'student')
        return s ? s.id.toString() : null
      }).filter(Boolean)

      window.dispatchEvent(new CustomEvent('command-centre:open', {
        detail: {
          submitterId: submitterId.toString(),
          order,
          tab: event.detail.tab || 'main'
        }
      }))
    }
  } else if (event.detail?.action === 'update_email') {
    const id = event.detail.enrollment_id
    const email = event.detail.email

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute('content')
      if (!csrfToken) throw new Error('CSRF token not found')

      const cohortIdentifier = props.programme.cohort?.slug || props.programme.cohort_id
      const res = await fetch(`/cohorts/${cohortIdentifier}/programmes/${props.programme.slug}/enrollments/${id}`, {
        method: 'PUT',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ student_email: email })
      })
      if (!res.ok) {
        console.error('Failed to update email on server')

        // Revert optimistic UI update by forcing a refresh or dispatching a reload
        const event = new CustomEvent('programme-action', {
          detail: { action: 'toast', message: 'Failed to update email. Refreshing...', type: 'error' },
          bubbles: true,
          composed: true
        })
        window.dispatchEvent(event)

        // Reload page to resync state since we don't have the old email
        setTimeout(() => window.location.reload(), 1500)
      }
    } catch (err) {
      console.error(err)
    }
  } else if (event.detail?.action === 'update_sponsor_email') {
    const email = event.detail.email

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute('content')
      if (!csrfToken) throw new Error('CSRF token not found')

      const cohortIdentifier = props.programme.cohort?.slug || props.programme.cohort_id || props.programme.cohort?.id
      const progIdentifier = props.programme.slug || props.programme.id
      const res = await fetch(`/cohorts/${cohortIdentifier}/programmes/${progIdentifier}/update_sponsor`, {
        method: 'PUT',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      })
      if (!res.ok) {
        throw new Error('Failed to update sponsor email on server')
      }

      await res.json()
      displayToast('Sponsor email updated successfully', 'success')
      setTimeout(() => { window.location.reload() }, 1000)
    } catch (err) {
      console.error(err)
      displayToast('Failed to update sponsor email', 'error')
    }
  } else if (event.detail?.action === 'dispatch_sponsor') {
    dispatchSponsor()
  } else if (event.detail?.action === 'toast') {
    displayToast(event.detail.message, event.detail.type || 'success')
  }
}

const handleEsc = (e) => {
  if (e.key === 'Escape' && showDeleteModal.value && !isDeleting.value) {
    closeDeleteModal()
  }
}

const handleGlobalClick = () => {
  isDropdownOpen.value = false
  isSortOpen.value = false
  isActionMenuOpen.value = false
  isActionsKebabOpen.value = false
  isMobileActionsKebabOpen.value = false
}

let pollInterval = null

const fetchFreshStatus = async () => {
  if (isProgrammeFullyCompleted.value) return
  if (isEditingName.value || isEmailModalOpen.value || isDeleting.value) return

  try {
    const cohortIdentifier = props.programme.cohort?.slug || props.programme.cohort_id || props.programme.cohort?.id
    const progIdentifier = props.programme.slug || props.programme.id
    if (!cohortIdentifier || !progIdentifier) return

    const res = await fetch(`/cohorts/${cohortIdentifier}/programmes/${progIdentifier}.json`, {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    if (!res.ok) return
    const data = await res.json()
    if (data.enrollments && Array.isArray(data.enrollments)) {
      localEnrollments.value = data.enrollments.filter(e => !deletedIds.value.has(e.id))
    }
    if (data.programme) {
      Object.assign(localProgramme.value, data.programme)
      Object.assign(props.programme, data.programme)
    }
  } catch (err) {
    // Non-blocking background poll
  }
}

const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    fetchFreshStatus()
  }
}

onMounted(() => {
  window.addEventListener('programme-action', handleProgrammeAction)
  window.addEventListener('keydown', handleEsc)
  window.addEventListener('click', handleGlobalClick)
  document.addEventListener('visibilitychange', handleVisibilityChange)
  pollInterval = setInterval(fetchFreshStatus, 5000)

  const urlParams = new URLSearchParams(window.location.search)
  const tab = urlParams.get('tab')
  const allKnownTabs = ['all', 'not_sent', 'sent', 'in_progress', 'completed', 'delivered', 'opened', 'declined', 'bounced', 'complained']
  if (allKnownTabs.includes(tab)) {
    activeTab.value = tab
    if (dropdownTabs.some(t => t.key === tab)) {
      selectedDropdownTab.value = tab
    }
  }

  if (urlParams.get('success') === 'bulk_signed') {
    displayToast('Progress: You as a trainer are done. Now proceed to send to Learners.')

    // Remove the query param from URL so it doesn't show again on refresh
    const newUrl = new URL(window.location.href)
    newUrl.searchParams.delete('success')
    window.history.replaceState({}, document.title, newUrl.toString())
  }
})

onUnmounted(() => {
  if (pollInterval) clearInterval(pollInterval)
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('programme-action', handleProgrammeAction)
  window.removeEventListener('keydown', handleEsc)
  window.removeEventListener('click', handleGlobalClick)
})

const deleteStudent = async () => {
  if (!studentToDelete.value || isDeleting.value) return

  if (!deleteReason.value || deleteReason.value.trim() === '') {
    deleteError.value = 'A reason for removal is required for the audit trail.'
    return
  }

  deleteError.value = ''
  isDeleting.value = true

  try {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute('content')
    if (!csrfToken) throw new Error('CSRF token not found')

    const cohortIdentifier = props.programme.cohort?.slug || props.programme.cohort_id
    const res = await fetch(`/cohorts/${cohortIdentifier}/programmes/${props.programme.slug}/enrollments/${studentToDelete.value.id}`, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ reason: deleteReason.value })
    })

    if (res.ok) {
      closeDeleteModal()
      displayToast('Student removed, recalculating progress...', 'success')
      setTimeout(() => {
        window.location.reload()
      }, 800)
    } else {
      const data = await res.json()
      displayToast(data.error || 'Failed to remove student', 'error')
    }
  } catch (error) {
    console.error(error)
    displayToast('Failed to remove student', 'error')
  } finally {
    isDeleting.value = false
  }
}

const emitSendForSignatures = () => {
  if ((localEnrollments.value || []).length > 0) {
    const cohortIdentifier = props.programme.cohort?.slug || props.programme.cohort_id || props.programme.cohort?.id
    const progIdentifier = props.programme.slug || props.programme.id
    window.location.href = `/cohorts/${cohortIdentifier}/programmes/${progIdentifier}/sign`
  }
}

const dispatchLearners = async () => {
  if (isDispatchingLearners.value || (localEnrollments.value || []).length === 0) return
  isDispatchingLearners.value = true
  try {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute('content')
    const cohortIdentifier = props.programme.cohort?.slug || props.programme.cohort_id || props.programme.cohort?.id
    const progIdentifier = props.programme.slug || props.programme.id
    const res = await fetch(`/cohorts/${cohortIdentifier}/programmes/${progIdentifier}/dispatch_learners`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }
    })

    if (res.ok) {
      displayToast('Invitation emails to learners are being dispatched.', 'success')
      props.programme.learners_dispatched_at = new Date().toISOString()
      const now = new Date().toISOString()
      localEnrollments.value.forEach(e => {
        const submitters = e.submission?.submitters || []
        const s = submitters.find(sub => sub.uuid?.toLowerCase() === 'student' || sub.email === e.student_email) || submitters[0]
        if (s && !s.sent_at && !s.completed_at) {
          s.sent_at = now
        }
      })
    } else {
      const data = await res.json().catch(() => ({}))
      displayToast(data.error || 'Failed to dispatch emails to learners.', 'error')
    }
  } catch (error) {
    console.error(error)
    displayToast('Failed to dispatch emails to learners.', 'error')
  } finally {
    isDispatchingLearners.value = false
  }
}

const dispatchSponsor = async () => {
  if (isDispatchingSponsor.value) return
  if (!props.programme.sponsor_email) {
    displayToast('Sponsor email is required.', 'error')
    return
  }

  isDispatchingSponsor.value = true
  try {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute('content')
    const cohortIdentifier = props.programme.cohort?.slug || props.programme.cohort_id || props.programme.cohort?.id
    const progIdentifier = props.programme.slug || props.programme.id
    const res = await fetch(`/cohorts/${cohortIdentifier}/programmes/${progIdentifier}/dispatch_sponsor`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ sponsor_email: props.programme.sponsor_email })
    })

    if (res.ok) {
      displayToast('Invitation email to sponsor is being dispatched.', 'success')
      props.programme.sponsor_dispatched_at = new Date().toISOString()
      const now = new Date().toISOString()
      localEnrollments.value.forEach(e => {
        const submitters = e.submission?.submitters || []
        const s = submitters.find(sub => sub.uuid?.toLowerCase().includes('sponsor') || sub.name?.toLowerCase().includes('sponsor'))
        if (s && !s.sent_at && !s.completed_at) {
          s.sent_at = now
        }
      })
    } else {
      const data = await res.json().catch(() => ({}))
      displayToast(data.error || 'Failed to dispatch email to sponsor.', 'error')
    }
  } catch (error) {
    console.error(error)
    displayToast('Failed to dispatch email to sponsor.', 'error')
  } finally {
    isDispatchingSponsor.value = false
  }
}

const primaryTabs = [
  { key: 'all', label: 'All' },
  { key: 'not_sent', label: 'Not sent' },
  { key: 'sent', label: 'Sent' },
  { key: 'in_progress', label: 'In progress' }
]

const dropdownTabs = [
  { key: 'completed', label: 'Completed' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'opened', label: 'Opened' },
  { key: 'declined', label: 'Declined' },
  { key: 'bounced', label: 'Email Bounced' },
  { key: 'complained', label: 'Spam Complaint' }
]

const allTabs = computed(() => [
  ...primaryTabs,
  ...dropdownTabs
])

// Mobile cards pagination (Loads 10 at a time with Load more button)
const mobileDisplayLimit = ref(10)
const mobileCardsContainerRef = ref(null)

const visibleMobileEnrollments = computed(() => {
  return filteredEnrollments.value.slice(0, mobileDisplayLimit.value)
})

const loadMoreMobileSubmissions = () => {
  if (mobileDisplayLimit.value < filteredEnrollments.value.length) {
    mobileDisplayLimit.value += 10
  }
}

// Progress modal and circular FAB
const isProgressModalOpen = ref(false)
const openProgressModal = () => {
  isProgressModalOpen.value = true
}
const closeProgressModal = () => {
  isProgressModalOpen.value = false
}

const totalSignaturesNeeded = computed(() => {
  return localEnrollments.value.length * 3
})

const isTrainerSubmitter = (s) => {
  if (!s) return false
  const uuids = props.programme.field_mapping?.trainer || ['trainer']
  return uuids.includes(s.uuid) || s.name?.toLowerCase() === 'trainer' || s.uuid?.toLowerCase().includes('trainer')
}

const isStudentSubmitter = (s, e) => {
  if (!s) return false
  const uuids = props.programme.field_mapping?.student || ['student']
  return s.email === e?.student_email || uuids.includes(s.uuid) || s.name?.toLowerCase() === 'student' || s.uuid?.toLowerCase().includes('student')
}

const isSponsorSubmitter = (s) => {
  if (!s) return false
  const uuids = props.programme.field_mapping?.sponsor || ['sponsor_0']
  if (uuids.includes(s.uuid)) return true
  if (s.uuid?.toLowerCase().includes('sponsor')) return true
  if (s.name?.toLowerCase() === 'sponsor') return true
  if (props.programme.sponsor_email && s.email && s.email.toLowerCase() === props.programme.sponsor_email.toLowerCase()) return true
  return false
}

const trainerDone = computed(() => {
  if (localEnrollments.value.length === 0) return false
  return localEnrollments.value.every(e => {
    const submitters = e.submission?.submitters || []
    return submitters.some(s => isTrainerSubmitter(s) && s.completed_at)
  })
})

const sponsorDone = computed(() => {
  if (localEnrollments.value.length === 0) return false
  return localEnrollments.value.every(e => {
    const submitters = e.submission?.submitters || []
    return submitters.some(s => isSponsorSubmitter(s) && s.completed_at)
  })
})

const totalSignaturesCompleted = computed(() => {
  const n = localEnrollments.value.length
  if (n === 0) return 0

  const trainerCount = localEnrollments.value.filter(e => {
    const submitters = e.submission?.submitters || []
    return submitters.some(s => isTrainerSubmitter(s) && s.completed_at)
  }).length

  const learnerCount = localEnrollments.value.filter(e => {
    const submitters = e.submission?.submitters || []
    return submitters.some(s => isStudentSubmitter(s, e) && s.completed_at)
  }).length

  const sponsorCount = localEnrollments.value.filter(e => {
    const submitters = e.submission?.submitters || []
    return submitters.some(s => isSponsorSubmitter(s) && s.completed_at)
  }).length

  return trainerCount + learnerCount + sponsorCount
})

const isProgrammeFullyCompleted = computed(() => {
  const n = localEnrollments.value.length
  if (n === 0) return false

  const learnersDone = localEnrollments.value.length > 0 && localEnrollments.value.every(e => {
    const submitters = e.submission?.submitters || []
    return submitters.some(s => isStudentSubmitter(s, e) && s.completed_at)
  })

  return trainerDone.value && learnersDone && sponsorDone.value
})

const overallProgressPercent = computed(() => {
  if (totalSignaturesNeeded.value === 0) return 0
  if (isProgrammeFullyCompleted.value) return 100

  const calculated = Math.round((totalSignaturesCompleted.value / totalSignaturesNeeded.value) * 100)
  return Math.min(99, calculated)
})

const progressStrokeColor = computed(() => {
  const p = overallProgressPercent.value
  if (p < 34) return '#ef4444' // red
  if (p < 67) return '#f97316' // orange
  return '#01d680' // green
})

const progressStrokeOffset = computed(() => {
  const circumference = 125.66 // 2 * pi * 20
  const p = overallProgressPercent.value
  return circumference - (circumference * p) / 100
})

const isActionMenuOpen = ref(false)
const toggleActionMenu = () => {
  isActionMenuOpen.value = !isActionMenuOpen.value
}
const closeActionMenu = () => {
  isActionMenuOpen.value = false
}

const currentActionState = computed(() => {
  if (isProgrammeFullyCompleted.value) {
    return {
      type: 'completed',
      title: 'Completed',
      description: 'All signatures for Training Provider, Learners, and Sponsor are complete.',
      actionText: null,
      icon: 'check',
      disabled: false,
      isPending: false
    }
  }

  if (props.programme.students_completed) {
    const isDispatched = !!props.programme.sponsor_dispatched_at || props.programme.sponsor_status === 'delivered'
    return {
      type: 'sponsor',
      title: isDispatched ? 'Sent to Sponsor' : 'Send to Sponsor',
      description: isDispatched
        ? 'Invitation email has already been dispatched to the sponsor.'
        : 'Dispatch signing invitation email to the sponsor.',
      actionText: isDispatchingSponsor.value ? 'Sending...' : (isDispatched ? 'Sent to Sponsor' : 'Send to Sponsor'),
      icon: 'send',
      disabled: isDispatched || isDispatchingSponsor.value,
      isPending: isDispatchingSponsor.value,
      handler: dispatchSponsor
    }
  }

  if (trainerDone.value && !props.programme.students_completed) {
    const isDispatched = !!props.programme.learners_dispatched_at
    return {
      type: 'learners',
      title: 'Send to Learners',
      description: isDispatched
        ? 'Invitation emails have already been dispatched to learners.'
        : 'Dispatch signing invitation emails to all learners in this cohort.',
      actionText: isDispatchingLearners.value ? 'Sending...' : (isDispatched ? 'Already Sent' : 'Send to Learners'),
      icon: 'send',
      disabled: isDispatched || isDispatchingLearners.value,
      isPending: isDispatchingLearners.value,
      handler: dispatchLearners
    }
  }

  return {
    type: 'trainer',
    title: 'Sign as Training Provider',
    description: 'Sign all learnership agreements as the Training Provider.',
    actionText: 'Sign as Training Provider',
    icon: 'sign',
    disabled: localEnrollments.value.length === 0,
    isPending: false,
    handler: emitSendForSignatures
  }
})

const executeFabAction = () => {
  if (currentActionState.value.handler && !currentActionState.value.disabled) {
    currentActionState.value.handler()
    isActionMenuOpen.value = false
  }
}

const activeTab = ref('all')
const isDropdownOpen = ref(false)
const selectedDropdownTab = ref('completed')
const searchQuery = ref('')

const isDropdownTabActive = computed(() => {
  return dropdownTabs.some(t => t.key === activeTab.value)
})

const currentDropdownLabel = computed(() => {
  const match = dropdownTabs.find(t => t.key === activeTab.value)
  if (match) return match.label
  const lastMatch = dropdownTabs.find(t => t.key === selectedDropdownTab.value)
  return lastMatch ? lastMatch.label : 'Completed'
})

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value
}

const selectDropdownTab = (key) => {
  selectedDropdownTab.value = key
  activeTab.value = key
  isDropdownOpen.value = false
  const url = new URL(window.location)
  url.searchParams.set('tab', activeTab.value)
  window.history.pushState({}, '', url)
}

const setTab = (tab) => {
  if ((localEnrollments.value || []).length === 0) return
  isDropdownOpen.value = false
  if (activeTab.value === tab) {
    activeTab.value = 'all' // Toggle off
  } else {
    activeTab.value = tab
  }
  const url = new URL(window.location)
  url.searchParams.set('tab', activeTab.value)
  window.history.pushState({}, '', url)
}

const currentSort = ref('date-desc')
const isSortOpen = ref(false)

const toggleSort = () => {
  isSortOpen.value = !isSortOpen.value
}

const setSort = (val) => {
  currentSort.value = val
  isSortOpen.value = false
}

watch([activeTab, searchQuery, currentSort], () => {
  mobileDisplayLimit.value = 10
})

const getRelevantDate = (e) => {
  const submitters = e.submission?.submitters || []
  const s = submitters.find(sub => sub.email === e.student_email || sub.uuid?.toLowerCase() === 'student')
  if (s) {
    if (s.declined_at) return new Date(s.declined_at)
    if (s.completed_at) return new Date(s.completed_at)
    if (s.started_at) return new Date(s.started_at)
    if (s.opened_at) return new Date(s.opened_at)
    if (s.sent_at) return new Date(s.sent_at)
  }
  return new Date(e.created_at || 0)
}

const filteredEnrollments = computed(() => {
  let result = localEnrollments.value || []

  // Tab filtering
  if (activeTab.value !== 'all') {
    result = result.filter(e => {
      const email = e.student_email || ''
      const submitters = e.submission?.submitters || []
      const s = submitters.find(sub => sub.email === email || sub.uuid?.toLowerCase() === 'student')

      let status = 'not_sent'
      if (s) {
        const deliveryStatus = s.metadata?.email_delivery_status
        if (deliveryStatus === 'complained' || s.metadata?.complained_at) status = 'complained'
        else if (deliveryStatus === 'bounced' || s.metadata?.bounced_at) status = 'bounced'
        else if (s.declined_at) status = 'declined'
        else if (s.completed_at) status = 'completed'
        else if (s.started_at) status = 'in_progress'
        else if (s.opened_at) status = 'opened'
        else if (deliveryStatus === 'delivered' || s.metadata?.delivered_at) status = 'delivered'
        else if (s.sent_at) status = 'sent'
        else status = 'not_sent'
      }

      return status === activeTab.value
    })
  }

  // Search filtering
  if (searchQuery.value.trim() !== '') {
    const query = searchQuery.value.toLowerCase()
    result = result.filter(e => {
      const learnerName = `${e.name || ''} ${e.surname || ''}`.toLowerCase()
      const folioName = (e.programme?.name || '').toLowerCase()
      return learnerName.includes(query) || folioName.includes(query)
    })
  }

  // Sorting
  result.sort((a, b) => {
    if (currentSort.value === 'name-asc') {
      const nameA = `${a.name || ''} ${a.surname || ''}`.toLowerCase()
      const nameB = `${b.name || ''} ${b.surname || ''}`.toLowerCase()
      return nameA.localeCompare(nameB)
    } else if (currentSort.value === 'name-desc') {
      const nameA = `${a.name || ''} ${a.surname || ''}`.toLowerCase()
      const nameB = `${b.name || ''} ${b.surname || ''}`.toLowerCase()
      return nameB.localeCompare(nameA)
    } else if (currentSort.value === 'date-desc') {
      return getRelevantDate(b) - getRelevantDate(a)
    } else if (currentSort.value === 'date-asc') {
      return getRelevantDate(a) - getRelevantDate(b)
    }
    return 0
  })

  return result
})

const currentPage = ref(1)
const itemsPerPage = ref(5) // Requested to be 5 for now

const totalPages = computed(() => Math.ceil(filteredEnrollments.value.length / itemsPerPage.value))

const startIndex = computed(() => {
  if (filteredEnrollments.value.length === 0) return 0
  return (currentPage.value - 1) * itemsPerPage.value
})

const endIndex = computed(() => {
  if (filteredEnrollments.value.length === 0) return 0
  return Math.min(startIndex.value + itemsPerPage.value, filteredEnrollments.value.length)
})

const paginatedEnrollments = computed(() => {
  const start = startIndex.value
  const end = start + itemsPerPage.value
  return filteredEnrollments.value.slice(start, end)
})

const prevPage = () => {
  if (currentPage.value > 1) currentPage.value--
}

const nextPage = () => {
  if (currentPage.value < totalPages.value) currentPage.value++
}

watch([activeTab, searchQuery], () => {
  currentPage.value = 1
})

watch(totalPages, (val) => {
  if (currentPage.value > val) {
    currentPage.value = Math.max(1, val)
  }
})
</script>
